import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { CopyFieldValueProps, useNotification } from 'spindl-shared-components';
import { capitalizeFirstLetter, copyTextToClipboard } from '../../../lib/utils';
import { Tag } from '../../../modules/schema/types';
import { getSchemaUseCase } from '../../pages/TemplateLibraryPage/TemplateLibraryPage.interactor';
import { TagsSideBarCombinedProps } from './types';

const usePresenter = (
  props: TagsSideBarCombinedProps,
): TagsSideBarCombinedProps => {
  const { t } = useTranslation();
  const { emailTemplate } = props;
  const schemaId = emailTemplate?.schemaProviderId;
  const { trigger } = useNotification();

  const { data } = useQuery(['getSchema', schemaId], () =>
    getSchemaUseCase(Number(schemaId)),
  );

  const [displayTags, setDisplayTags] = useState<CopyFieldValueProps[]>([]);

  const transformDataVariablesToElementArray = useCallback((
    variables: Tag[],
  ): CopyFieldValueProps[] =>
    variables.map((tag: Tag) => ({
      text: {
        value: `{{${tag.variableLabel}}}`,
      },
      button: {
        icon: {
          asset: 'Copy',
        },
        onClick: () => copyTextToClipboard(`{{${tag.variableLabel}}}`, trigger),
      },
    })), [trigger]);

  const tagItems: CopyFieldValueProps[] | undefined = useMemo(() => {
    if (!data) {
      return undefined;
    }
    return transformDataVariablesToElementArray(data.variables);
  }, [data, transformDataVariablesToElementArray]);

  useEffect(() => {
    if (!data || !tagItems) {
      return;
    }

    setDisplayTags(tagItems);
  }, [data, tagItems]);

  const handleSubmitQuery = (searchQuery: string) => {
    if (!data || !tagItems) {
      return;
    }

    const cleanSearchQuery = searchQuery.trim().toLowerCase();

    const tagsToDisplay: CopyFieldValueProps[] = tagItems.filter((tagItem) =>
      (tagItem.text?.value as string).toLowerCase().includes(cleanSearchQuery),
    );

    setDisplayTags(tagsToDisplay);
  };

  props = {
    ...props,
    text: {
      value: t('side_bar.tags.title'),
    },
    text1: {
      value: data ? `${capitalizeFirstLetter(data.serviceName)} tags` : undefined,
    },
    searchBar: {
      textPlaceholder: t('side_bar.tags.search_placeholder'),
      icon: {
        asset: 'Search',
      },
      onSubmit: handleSubmitQuery,
      clearOnBlur: true,
    },
    copyFieldList: {
      copyFields: displayTags,
    },
  };

  return props;
};

export default usePresenter;
